import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import DashboardContent from "../../components/DashboardContent";
import PatientNarrativeQuestionLabel from "../../components/PatientNarrativeQuestionLabel";
import RadioGroup from "../../components/RadioGroup";
import CheckboxGroupWithOther from "../../components/CheckboxGroupWithOther";
import Loader from "../../utils/Loader";
import usePatientNarrativeWizardSection from "../../hooks/usePatientNarrativeWizardSection";
import usePatientNarrativeQuestions from "../../hooks/usePatientNarrativeQuestions";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import { PATIENT_NARRATIVE_WIZARD_SECTIONS } from "../../utils/patientNarrativeWizardSections";
import {
  describeSaveError,
  describeLoadError,
} from "../../utils/patientNarrativeErrors";
import questionsData from "../../assets/json/patient_narrative_questions_data.json";

const SECTION = PATIENT_NARRATIVE_WIZARD_SECTIONS.sources;

const Sources = () => {
  const { studyId: patientNarrativeId } = useParams();
  const { hasPermission } = usePermissions();

  // pn:wizards:read gates the GET that pre-fills this section; writing it back
  // needs pn:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.pnWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.pnWizardsUpdate);
  const {
    answers,
    loading,
    saving,
    error,
    updateFields,
    save,
    hasMissingRequiredFields,
  } = usePatientNarrativeWizardSection(patientNarrativeId, SECTION, {
    canRead: canReadWizards,
    canSave: canSaveWizards,
  });
  const {
    questions,
    loading: questionsLoading,
    error: questionsError,
  } = usePatientNarrativeQuestions();
  const [saveError, setSaveError] = useState("");

  const handleSave = async event => {
    event?.preventDefault?.();
    setSaveError("");
    try {
      await save();
      return { success: true };
    } catch (err) {
      setSaveError(describeSaveError(err));
      return { success: false };
    }
  };

  // A failed GET leaves no answers to edit - show why instead of the loader.
  if (loading || questionsLoading || !answers) {
    return (
      <DashboardContent
        sidebarCSR={true}
        headerTitle={"Sources"}
        pageContent={
          !loading && error ? (
            <span className={"errmsg"} aria-live="assertive">
              {describeLoadError(error)}
            </span>
          ) : (
            <Loader loading={true} />
          )
        }
      />
    );
  }

  const PageContent = (
    <div className={"questions-content pn-sources"}>
      {error && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(error)}
        </span>
      )}
      {questionsError && (
        <span className={"errmsg"} aria-live="assertive">
          {describeLoadError(questionsError)}
        </span>
      )}
      {saveError && (
        <span className={"errmsg"} aria-live="assertive">
          {saveError}
        </span>
      )}

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>1</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qSourceConflictResolution"}
          />
          <RadioGroup
            name={"qSourceConflictResolution"}
            value={answers.qSourceConflictResolution}
            onChange={value =>
              updateFields({ qSourceConflictResolution: value })
            }
            options={questionsData.choices.qSourceConflictResolution}
            disabled={saving}
          />
        </div>
      </div>

      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>2</span>
        </div>
        <div className={"questions-content__item"}>
          <PatientNarrativeQuestionLabel
            section={SECTION}
            questions={questions}
            field={"qSupplementaryListings"}
          />
          <CheckboxGroupWithOther
            name={"qSupplementaryListings"}
            value={answers.qSupplementaryListings}
            onChange={value => updateFields({ qSupplementaryListings: value })}
            options={questionsData.choices.qSupplementaryListings}
            exclusiveValue={"cioms"}
            disabled={saving}
          />
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Sources"}
      pageContent={PageContent}
      prevButton={{
        buttonTitle: "Patient Scope",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/patient-scope",
      }}
      nextButton={{
        buttonTitle: "Narrative Format",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/narrative-format",
        disabled: hasMissingRequiredFields,
      }}
      saveButton={true}
      savePermission={[PERMISSIONS.pnWizardsRead, PERMISSIONS.pnWizardsUpdate]}
      saveButtonHandler={handleSave}
    />
  );
};

export default Sources;
